import mixpanel from 'mixpanel-browser';
import * as amplitude from '@amplitude/analytics-browser';
import { setUtm } from '../exports/Cookies';
import { disableSentryReplay } from './SentryReplay';

// GDPR Art. 7(3): withdrawing consent must be as easy as giving it. The footer's "Cookie settings"
// reopens the banner (via this event), and declining after an earlier accept clears what the
// trackers left on the device, then reloads so the already-loaded gtag.js / fbevents.js are gone.

export const COOKIE_SETTINGS_EVENT = 'foodamigos:open-cookie-settings';

// Cookies set by Mixpanel (mp_*), Amplitude (AMP_*), Google Analytics (_ga*, _gid, _gat*), Google Ads
// conversion tracking (_gcl_*) and the Meta Pixel (_fbp, _fbc), plus our own `utm` attribution cookie.
const TRACKING_COOKIE = /^(mp_|AMP_|_ga|_gid|_gat|_gcl_|_fbp|_fbc|utm$)/;

// localStorage / sessionStorage keys written for tracking: Amplitude's queues, our analytics user id
// (`company-<id>-analytics_user_id`), Mixpanel's id hand-off and the once-per-session event flags
// (view_menu and the `analytics_dedupe:*` offer/bonus markers).
const TRACKING_STORAGE_KEY = /^(AMP_|amp_|mp_|__mp)|analytics_user_id$|^is_view_storefront_tracked_|^analytics_dedupe:/;

export function openCookieSettings(): void {
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
}

// Mixpanel writes its cookie on the parent domain (cross_subdomain_cookie), so a host-only delete
// would miss it. Expire each name on the host and on every parent domain.
function domainCandidates(hostname: string): (string | null)[] {
  const parts = hostname.split('.');
  const parents = parts.slice(0, -1).map((_, index) => `.${parts.slice(index).join('.')}`);

  return [null, ...parents];
}

function expireCookie(name: string, domain: string | null): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`;
}

function clearStorage(storage: Storage): void {
  Object.keys(storage)
    .filter((key) => TRACKING_STORAGE_KEY.test(key))
    .forEach((key) => storage.removeItem(key));
}

/** Removes every tracking cookie and storage key from this device. */
export function clearTrackingStorage(): void {
  const names = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0].trim())
    .filter((name) => TRACKING_COOKIE.test(name));

  names.forEach((name) => domainCandidates(window.location.hostname).forEach((domain) => expireCookie(name, domain)));

  try {
    clearStorage(window.localStorage);
    clearStorage(window.sessionStorage);
  } catch {
    // Storage can be unavailable (private mode); the cookies are what matters most.
  }
}

/** Stops every tracker, wipes what they stored and reloads the page. */
export function withdrawTrackingConsent(reload: () => void = () => window.location.reload()): void {
  try {
    if (mixpanel?.['__loaded']) {
      mixpanel.stop_session_recording?.();
      mixpanel.reset();
    }

    amplitude.setOptOut(true);
    amplitude.reset();
  } catch {
    // An SDK that never initialised can throw; clearing storage below still runs.
  }

  disableSentryReplay();
  setUtm(null);
  clearTrackingStorage();
  reload();
}
